import { useMemo } from 'react';
import { WebsocketProvider } from 'y-websocket';
import * as Y from '../../../shared/yjs';
import { create } from 'zustand';
import { StoreApi, UseBoundStore } from 'zustand';
import { EditorView } from 'prosemirror-view';

import { entityService } from '../../../services/entityService';
// import { DocEntity } from '../../../types';
import { docsCRDTService } from '../services/crdt/docsCRDTService';
import { ObjectId } from 'bson';
import { DocEntity } from '../types/docs';
import { createFileLogger, formatJSON } from '../../../utils/logger';
import { yDocumentService } from '../services/doc/internal/yDocLowLevelAPI';
import { summaryService } from '../services/features/ai/summaryService';
import { stableNodeRegistry } from '../services/registries/stableNodeRegistry';
import { generateDocumentPrefix } from '../../../utils/entityIdGenerator';


const log = createFileLogger('DOCS_APP_STORE', { enabled: false });

interface YxmlNode {
  content: string;
  type: string;
  pos: number;
  size: number;
  isDeletion: boolean;
  isInsertion: boolean;
}

// Define the state interface
export interface DocsAppState {
  windowId: string;
  spaceId: string;
  lifecycle: 'uninitialized' | 'loading' | 'ready' | 'error';
  status: 'idle' | 'loading' | 'error'; // @deprecated Use lifecycle instead
  error: Error | null;
  docContent: string;
  initialized: boolean; // @deprecated Use lifecycle === 'ready' instead
  isInitializing: boolean; // @deprecated Use lifecycle === 'loading' instead
  isInitialized: boolean; // @deprecated Use lifecycle === 'ready' instead
  cleanup?: () => void;
  docId: string | null;
  recentDocs: DocEntity[];
  ydoc: Y.Doc | null;
  provider?: WebsocketProvider;
  editorView: EditorView | null;
  storeId: number;
  zoomLevel: number; // New: Store zoom level (100 = 100%)
}

// Define the store interface
export interface DocsAppStore extends DocsAppState {
  // Internal mutation funnel
  _applyLocalUpdate: (updates: Partial<DocsAppState>, options?: {
    log?: boolean;
    syncCRDT?: boolean;
    source?: 'local' | 'crdt' | 'event';
  }) => void;
  
  createDocId: () => string;
  initialize: (docId: string, docEntity?: DocEntity) => Promise<void>;
  cleanup: () => void;
  fetchRecentDocs: () => Promise<void>;
  createDoc: () => Promise<DocEntity>;
  setState: (state: Partial<DocsAppState>) => void;
  setDocId: (docId: string | null) => void;
  setEditorView: (editorView: EditorView | null) => void;
  editorViewGetCurrentText: () => string;
  
  // New: Zoom functions
  setZoomLevel: (level: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  persistZoomLevel: (level: number) => void;
  loadZoomLevel: (docEntity?: any) => void;

  // Diff interface with nodes
  getNodes: (view?: EditorView) => YxmlNode[];
  addNode: (node: Partial<YxmlNode>) => YxmlNode[] | null;
  removeNode: (position: number) => YxmlNode[] | null;
  updateNode: (node: Partial<YxmlNode>) => YxmlNode[] | null;
  
  // Summary methods
  updateSummary: (force?: boolean) => Promise<void>;
  getSummaryStatus: () => { pending: boolean; summary?: DocEntity['skeleton']['summary'] };

  // ✅ NEW: Actions pattern
  actions: {
    initialize: (docId: string, docEntity?: DocEntity) => Promise<void>;
    createDoc: () => Promise<DocEntity>;
    fetchRecentDocs: () => Promise<void>;
    setDocId: (docId: string | null) => void;
    setZoomLevel: (level: number) => void;
    zoomIn: () => void;
    zoomOut: () => void;
    resetZoom: () => void;
    updateSummary: (force?: boolean) => Promise<void>;
    addNode: (node: Partial<YxmlNode>) => YxmlNode[] | null;
    removeNode: (position: number) => YxmlNode[] | null;
    updateNode: (node: Partial<YxmlNode>) => YxmlNode[] | null;
    setEditorView: (editorView: EditorView | null) => void;
    editorViewGetCurrentText: () => string;
  };
}

// Define the store hook type
export type DocsAppStoreHook = UseBoundStore<StoreApi<DocsAppStore>>;

// First, update the store type to include the subscription
interface DocsStore extends DocsAppStore {
  subscribe: (subscriber: (state: DocsAppState) => void) => () => boolean;
}

// Create the store with proper typing
export const createDocsAppStore = (windowId: string, spaceId: string) => {
  // Create a set of subscribers
  const subscribers = new Set<(state: DocsAppState) => void>();

  // Create the base store
  return create<DocsStore>((set, get) => ({
    // Initial state
    windowId,
    spaceId,
    lifecycle: 'uninitialized',
    status: 'idle', // @deprecated
    docContent: '',
    initialized: false, // @deprecated
    isInitializing: false, // @deprecated
    isInitialized: false, // @deprecated
    recentDocs: [],
    docId: null,
    error: null,
    ydoc: null,
    editorView: null,
    storeId: Date.now(),
    zoomLevel: 100, // Default zoom is 100% for new documents

    // ✅ NEW: Single mutation funnel (internal helper)
    _applyLocalUpdate: (updates: Partial<DocsAppState>, options: {
      log?: boolean;
      syncCRDT?: boolean;
      source?: 'local' | 'crdt' | 'event';
    } = {}) => {
      const { log: shouldLog = true, syncCRDT = true, source = 'local' } = options;
      
      // Dev assertions
      if (process.env.NODE_ENV === 'development') {
        if (source === 'crdt' && syncCRDT !== false) {
          console.error('[docsAppStore] ⚠️ CRDT loop risk: source=crdt but syncCRDT=true');
        }
      }
      
      // Normalize updates
      const normalized = { ...updates };
      
      // Sync deprecated fields with lifecycle
      if (normalized.lifecycle !== undefined) {
        switch (normalized.lifecycle) {
          case 'loading':
            normalized.isInitializing = true;
            normalized.isInitialized = false;
            normalized.initialized = false;
            normalized.status = 'loading';
            break;
          case 'ready':
            normalized.isInitializing = false;
            normalized.isInitialized = true;
            normalized.initialized = true;
            normalized.status = 'idle';
            break;
          case 'error':
            normalized.isInitializing = false;
            normalized.isInitialized = false;
            normalized.initialized = false;
            normalized.status = 'error';
            break;
          case 'uninitialized':
            normalized.isInitializing = false;
            normalized.isInitialized = false;
            normalized.initialized = false;
            normalized.status = 'idle';
            break;
        }
      }
      
      // CRDT sync (if needed)
      if (syncCRDT && source === 'local' && get().ydoc) {
        if (normalized.zoomLevel !== undefined) {
          const ydoc = get().ydoc;
          const docStyleMap = ydoc!.getMap('doc_style');
          docStyleMap.set('zoomLevel', normalized.zoomLevel);
        }
      }
      
      // Log update
      if (shouldLog && process.env.NODE_ENV === 'development') {
        log.log('[_applyLocalUpdate]', { updates: Object.keys(normalized), source });
      }
      
      // Update state
      set((state) => ({ ...state, ...normalized }));
      
      // Notify subscribers
      subscribers.forEach(subscriber => subscriber({ ...get(), ...normalized }));
    },

    // New: Zoom control functions
    setZoomLevel: (level: number) => {
      // Clamp zoom level between 50% and 150%
      const clampedLevel = Math.max(50, Math.min(150, level));
      log.log('[DocsAppStore] Setting zoom level to', clampedLevel);
      get()._applyLocalUpdate({ zoomLevel: clampedLevel }, { syncCRDT: true, log: false });
    },
    
    zoomIn: () => {
      const { zoomLevel } = get();
      const newZoomLevel = Math.min(150, zoomLevel + 10);
      log.log('[DocsAppStore] Zooming in from', zoomLevel, 'to', newZoomLevel);
      get()._applyLocalUpdate({ zoomLevel: newZoomLevel }, { syncCRDT: true, log: false });
    },
    
    zoomOut: () => {
      const { zoomLevel } = get();
      const newZoomLevel = Math.max(50, zoomLevel - 10);
      log.log('[DocsAppStore] Zooming out from', zoomLevel, 'to', newZoomLevel);
      get()._applyLocalUpdate({ zoomLevel: newZoomLevel }, { syncCRDT: true, log: false });
    },
    
    resetZoom: () => {
      log.log('[DocsAppStore] Resetting zoom to 100%');
      get()._applyLocalUpdate({ zoomLevel: 100 }, { syncCRDT: true, log: false });
    },

    // ✅ NEW: Persist zoom level to Y.js document settings
    persistZoomLevel: (level: number) => {
      const { ydoc } = get();
      if (!ydoc) {
        log.warn('[DocsAppStore] Cannot persist zoom - no Y.js document available');
        return;
      }

      try {
        const docStyleMap = ydoc.getMap('doc_style');
        docStyleMap.set('zoomLevel', level);
        log.log('[DocsAppStore] Persisted zoom level to Y.js:', level);
      } catch (error) {
        log.error('[DocsAppStore] Error persisting zoom level:', error);
      }
    },

    // ✅ NEW: Load zoom level from Y.js document settings
    loadZoomLevel: (docEntity?: any) => {
      const { ydoc } = get();
      if (!ydoc) {
        log.warn('[DocsAppStore] Cannot load zoom - no Y.js document available');
        return;
      }

      try {
        const docStyleMap = ydoc.getMap('doc_style');
        const savedZoom = docStyleMap.get('zoomLevel');
        
        if (typeof savedZoom === 'number' && savedZoom >= 50 && savedZoom <= 150) {
          get()._applyLocalUpdate({ zoomLevel: savedZoom }, { syncCRDT: false, log: false });
        } else {
          // Check if the document entity has a zoom level setting in its skeleton
          const docEntityZoomLevel = docEntity?.skeleton?.settings?.zoomLevel;
          const defaultZoomLevel = typeof docEntityZoomLevel === 'number' ? docEntityZoomLevel : 90;
          
          log.log('[DocsAppStore] No valid saved zoom level found, using', defaultZoomLevel + '%', 
            docEntityZoomLevel ? '(from entity settings)' : '(default)');
          // Don't call persistZoomLevel here to avoid infinite loops during initialization
          get()._applyLocalUpdate({ zoomLevel: defaultZoomLevel }, { syncCRDT: false, log: false });
        }
      } catch (error) {
        log.error('[DocsAppStore] Error loading zoom level:', error);
        const docEntityZoomLevel = docEntity?.skeleton?.settings?.zoomLevel;
        const fallbackZoomLevel = typeof docEntityZoomLevel === 'number' ? docEntityZoomLevel : 90;
        get()._applyLocalUpdate({ zoomLevel: fallbackZoomLevel }, { syncCRDT: false, log: false });
      }
    },

    createDocId: () => new ObjectId().toString(),
    // Set state function
    setState: (newState: Partial<DocsAppState>) => {
      get()._applyLocalUpdate(newState, { syncCRDT: false, log: false });
    },
    setDocId: (docId: string | null) => {
      if (docId === null) {
        // Clear all document-related state when setting to null
        log.log('[DocsAppStore] Clearing document state');
        get()._applyLocalUpdate({ 
          docId: null,
          docContent: '',
          lifecycle: 'uninitialized',
          ydoc: null,
          provider: undefined,
          editorView: null,
          cleanup: undefined
        }, { syncCRDT: false, log: false });
      } else {
        log.log('[DocsAppStore] Setting new document ID:', docId);
        get()._applyLocalUpdate({ docId }, { syncCRDT: false, log: false });
        
        // 🚨 CRITICAL: Notify SpaceChat when document changes
        setTimeout(() => {
          try {
            const event = new CustomEvent('document-opened', {
              detail: {
                windowId,
                docId,
                appType: 'docs',
                reason: 'setDocId_called'
              }
            });
            window.dispatchEvent(event);
            
            log.log('📄 [DOCUMENT_CHANGE] Dispatched document opened event:', {
              windowId,
              docId,
              reason: 'document_id_changed'
            });
          } catch (error) {
            console.error('❌ [DOCUMENT_CHANGE] Failed to dispatch document opened event:', error);
          }
        }, 100); // Small delay to ensure state is updated
      }
    },
    setEditorView: (editorView: EditorView | null) => {
      const currentState = get();
      
      // Prevent setting the same editor view multiple times
      if (currentState.editorView === editorView) {
        return;
      }
      
      // Clean up previous editor view if it exists
      if (currentState.editorView && editorView !== null) {
        const log = createFileLogger('DOCS_APP_STORE');
        log.info('Replacing existing editor view');
      }
      
      const log = createFileLogger('DOCS_APP_STORE');
      log.log(formatJSON({ 
        hasEditorView: !!editorView,
        windowId: currentState.windowId 
      }, 'Setting editor view'));
      get()._applyLocalUpdate({ editorView }, { syncCRDT: false, log: false });
    },

    // Get current text content without throttling to ensure accurate content retrieval
    editorViewGetCurrentText: () => {
      const { editorView, windowId, spaceId } = get();
      if (editorView) {
        const textContent = editorView.state.doc.textContent;
        // Only log occasionally to prevent spam
        if (Math.random() < 0.05) { // Log only 5% of calls to reduce noise
          log.log(formatJSON({ 
            text: textContent.substring(0, 50) + '...', 
            contentLength: textContent.length,
            windowId, 
            spaceId 
          }, 'Getting current text'));
        }
        return textContent;
      } else {
        // Return empty string instead of throwing error during component transitions
        log.warn('No editor view found during component transition', { windowId: get().windowId, spaceId: get().spaceId });
        return '';
      }
    },

    // Initialize function
    initialize: async (docId: string, docEntity?: DocEntity) => {
      const initStartTime = Date.now();
      const initTraceId = `${docId.slice(-8)}_${Date.now()}`;
      
      log.log(`🔵 [INIT_TRACE:${initTraceId}] === STORE.INITIALIZE START ===`, {
        docId,
        windowId,
        spaceId,
        hasDocEntity: !!docEntity,
        timestamp: new Date().toISOString(),
        traceId: initTraceId
      });
      
      // Prevent duplicate initialization BEFORE logging
      const currentState = get();
      
      log.log(`🔍 [INIT_TRACE:${initTraceId}] Current state check:`, {
        isInitializing: currentState.isInitializing,
        isInitialized: currentState.isInitialized,
        currentDocId: currentState.docId,
        requestedDocId: docId,
        hasYdoc: !!currentState.ydoc,
        lifecycle: currentState.lifecycle
      });
      
      // ✅ STRENGTHEN: Block if SAME docId AND (initializing OR ready)
      // This prevents duplicate calls during initialization AND after completion
      if (currentState.docId === docId) {
        if (currentState.lifecycle === 'loading') {
          log.log(`🛡️ [INIT_TRACE:${initTraceId}] BLOCKED: Already loading ${docId.slice(-8)}`);
          return;
        }
        if (currentState.lifecycle === 'ready' && currentState.ydoc && currentState.provider) {
          log.log(`🛡️ [INIT_TRACE:${initTraceId}] BLOCKED: Already ready ${docId.slice(-8)}`);
          return;
        }
      }
      
      // Legacy checks for backward compatibility
      if (currentState.isInitializing && currentState.docId === docId) {
        log.log(`⚠️ [INIT_TRACE:${initTraceId}] SKIPPED: Already initializing this doc`);
        return;
      }
      if (currentState.isInitialized && currentState.docId === docId && currentState.ydoc) {
        log.log(`⚠️ [INIT_TRACE:${initTraceId}] SKIPPED: Already fully initialized this doc`);
        return;
      }
      log.log(formatJSON({ docId, windowId }, 'Starting initialization for doc'));

      try {
        log.log(`🔄 [INIT_TRACE:${initTraceId}] Setting lifecycle to 'loading'`);
        get()._applyLocalUpdate({ 
          lifecycle: 'loading',
          docId
        }, { syncCRDT: false });

        // ✅ UNIFIED: Use entityResolver for consistent document access (same as AI commands)
        let initialDoc = docEntity;
        let resolvedDocId = docId;
        
        log.log(`📄 [INIT_TRACE:${initTraceId}] Starting document resolution:`, {
          hasInitialDoc: !!initialDoc,
          docId
        });
        
        if (!initialDoc) {
          try {
            const { entityResolver } = await import('../../../services/entityResolver');
            const mockContext = {
              spaceId: spaceId || 'current-space',
              executionId: 'docs-app-store-init',
              userId: 'current-user',
              threadId: 'system',
              spaceContext: { spaceId: spaceId || 'current-space' },
              model: 'system',
              timestamp: Date.now(),
              services: {}
            } as any;
            
            // Use entityResolver to get consistent document ID resolution
            const resolved = await entityResolver.resolveEntity(docId, mockContext);
            initialDoc = resolved.entity as any; // Type assertion for compatibility
            resolvedDocId = resolved.entity._id || resolved.entity.id || docId;
            
            log.log('🔍 [DocsAppStore] Document resolved via entityResolver:', {
              originalDocId: docId,
              resolvedDocId: resolvedDocId,
              entityName: initialDoc?.name,
              source: resolved.source
            });
          } catch (error) {
            console.warn('[DocsAppStore] entityResolver failed, falling back to direct entityService:', error);
            try {
              initialDoc = await entityService.get(docId) as any;
              resolvedDocId = docId;
            } catch (entityError) {
              // Re-throw DocumentAccessError and other access-related errors
              const { DocumentAccessError } = await import('../../../types/spaces');
              if (entityError instanceof DocumentAccessError || 
                  (entityError as any)?.status === 403 ||
                  (entityError instanceof Error && entityError.message.includes('403'))) {
                throw entityError;
              }
              // For other errors, continue with initialization (this maintains existing behavior)
              console.error('[DocsAppStore] Failed to load document entity:', entityError);
            }
          }
        } else {
          // ✅ Using cached document from DocumentAccessWrapper
          // This is safe because DocumentAccessWrapper always fetches fresh (includes base64)
          log.log('🔍 [DocsAppStore] ✅ Using document from DocumentAccessWrapper (already has base64)');
          resolvedDocId = initialDoc._id || initialDoc.id || docId;
        }
        
        if (initialDoc) {
          log.log(`[DocsAppStore] Document loaded: ${initialDoc.name} (${docId.slice(-8)})`);
        } else {
          console.error('🔍 [DocsAppStore] Failed to load document from backend:', docId);
          throw new Error(`Failed to load document: ${docId}`);
        }

        // Ensure document has a docMiniId for node prefix generation
        let updatedDoc = initialDoc;
        if (initialDoc && !initialDoc.skeleton?.docMiniId) {
          // Generate and persist docMiniId
          const docMiniId = generateDocumentPrefix();
          log.log(`[DocsAppStore] Generating docMiniId for new document: ${docMiniId}`);
          
          try {
            await entityService.mergeUpdate(resolvedDocId, {
              skeleton: {
                docMiniId
              }
            });
            
            // Update our local copy
            updatedDoc = {
              ...initialDoc,
              skeleton: {
                ...initialDoc?.skeleton,
                docMiniId
              }
            };
            
            log.log(`[DocsAppStore] Persisted docMiniId: ${docMiniId}`);
          } catch (error) {
            log.error(`[DocsAppStore] Failed to persist docMiniId:`, error);
          }
        }

        // Set the document prefix in stableNodeRegistry
        const docMiniId = updatedDoc?.skeleton?.docMiniId;
        if (docMiniId) {
          stableNodeRegistry.setDocumentPrefix(resolvedDocId, docMiniId);
          log.log(`[DocsAppStore] Set document prefix: ${docMiniId} for docId: ${resolvedDocId.slice(-8)}`);
        }

        log.log(formatJSON({
          resolvedDocId,
          docName: updatedDoc?.name,
          hasContent: !!(updatedDoc?.skeleton?.content),
          hasBase64: !!(updatedDoc?.skeleton?.base64),
          contentLength: updatedDoc?.skeleton?.content?.length || 0,
          base64Length: updatedDoc?.skeleton?.base64?.length || 0,
          base64Data: updatedDoc?.skeleton?.base64, // Will be auto-truncated
          htmlContent: updatedDoc?.skeleton?.content, // Will be auto-truncated
          skeletonVersion: updatedDoc?.skeleton?.version,
          skeletonMetadata: updatedDoc?.skeleton?.metadata,
          entityType: updatedDoc?.entityType,
          updatedAt: updatedDoc?.updated_at
        }, `🔧 [INIT_TRACE:${initTraceId}] Calling docsCRDTService.getYDocForDoc`));
        
        const {doc: ydoc, provider} = await docsCRDTService.getYDocForDoc(
          resolvedDocId, 
          updatedDoc?.skeleton?.content || '',
          updatedDoc?.skeleton?.base64 || '',
          updatedDoc?.name, // Pass the document name
          false // Welcome image flag (removed skeleton dependency)
        );
        
        log.log(`✅ [INIT_TRACE:${initTraceId}] Y.js doc created:`, {
          hasYdoc: !!ydoc,
          hasProvider: !!provider,
          providerConnected: provider?.wsconnected,
          providerSynced: provider?.synced,
          ydocClientId: ydoc?.clientID,
          yxmlLength: ydoc?.getXmlFragment('prosemirror')?.length
        });

        // Don't set up a separate Y.js observer here - the ySyncPlugin in the editor handles this
        // Having both causes race conditions and DOM conflicts
        const cleanup = () => {
          // No Y.js observer to clean up since ySyncPlugin handles synchronization
        };

        // Get initial content from Y.js document using SAFE extraction
        let initialContent = '';
        try {
          // 🚨 CRITICAL FIX: Use SafeYJSExtraction instead of contaminated getPreviewText()
          const { SafeYJSExtraction } = await import('../../../shared/utils/yjsTextExtraction');
          const { UniversalSchema: schema } = await import('../../../services/baseApplication/schema/UniversalSchema');
          
          const safeResult = SafeYJSExtraction.extractContent(ydoc, schema, {
            fragmentKey: 'prosemirror',
            filterForAI: false, // Don't filter for initialization (show all content)
            returnBoth: false
          });
          
          if (safeResult.success) {
            initialContent = safeResult.plainText || '';
            log.log('[DocsAppStore.initialize] ✅ Used safe extraction for preview text:', {
              contentLength: initialContent.length,
              noContamination: true,
              isEmpty: !initialContent
            });
          } else {
            console.error('[DocsAppStore.initialize] 🚨 CRITICAL: Safe extraction failed during initialization!', safeResult.error);
            
            // 🚨 SALVAGE STRATEGY: Use fallback with warning instead of empty content
            // Empty content makes document appear broken, fallback keeps it functional
            console.warn('[DocsAppStore.initialize] 🚨 Using potentially contaminated fallback - document preview may have issues');
            initialContent = yDocumentService.getPreviewText(
              ydoc,
              Number.MAX_SAFE_INTEGER,
              { windowId, docId, logPrefix: '[DocsAppStore.initialize-FALLBACK]' }
            );
          }
        } catch (importError) {
          console.error('[DocsAppStore.initialize] 🚨 CRITICAL: Could not import safe extraction:', importError);
          
          // 🚨 SALVAGE STRATEGY: Use fallback instead of breaking initialization
          console.warn('[DocsAppStore.initialize] 🚨 Using fallback due to import failure - may have contamination');
          initialContent = yDocumentService.getPreviewText(
            ydoc,
            Number.MAX_SAFE_INTEGER,
            { windowId, docId, logPrefix: '[DocsAppStore.initialize-IMPORT-FALLBACK]' }
          );
        }

        const initDuration = Date.now() - initStartTime;
        
        log.log(`✅ [INIT_TRACE:${initTraceId}] Initialization complete, setting state to 'ready':`, {
          docId: resolvedDocId,
          originalDocId: docId,
          contentLength: initialContent.length,
          hasProvider: !!provider,
          hasYdoc: !!ydoc,
          windowId,
          durationMs: initDuration,
          providerState: {
            connected: provider?.wsconnected,
            synced: provider?.synced
          }
        });

        log.log(formatJSON({ 
          docId: resolvedDocId,
          originalDocId: docId, 
          contentLength: initialContent.length,
          hasProvider: !!provider,
          windowId
        }, 'Initialization complete'));

        get()._applyLocalUpdate({
          windowId,
          spaceId,
          lifecycle: 'ready',
          docId: resolvedDocId, // ✅ UNIFIED: Use resolved document ID consistently
          docContent: initialContent,
          ydoc,
          provider,
          cleanup
        }, { syncCRDT: false });
        
        log.log(`🎯 [INIT_TRACE:${initTraceId}] State updated to 'ready', scheduling zoom level load`);
        
        // ✅ NEW: Load saved zoom level after Y.js document is available
        setTimeout(() => {
          log.log(`📐 [INIT_TRACE:${initTraceId}] Loading zoom level from Y.js`);
          get().loadZoomLevel(updatedDoc);
        }, 100); // Small delay to ensure Y.js document is fully ready
        
        log.log(`🟢 [INIT_TRACE:${initTraceId}] === STORE.INITIALIZE COMPLETE (${initDuration}ms) ===`);
      } catch (error) {
        const initDuration = Date.now() - initStartTime;
        console.error(`❌ [INIT_TRACE:${initTraceId}] === STORE.INITIALIZE FAILED (${initDuration}ms) ===`, {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          docId,
          windowId,
          spaceId
        });
        
        log.error('Error initializing:', error);
        get()._applyLocalUpdate({ 
          lifecycle: 'error', 
          error: error as Error
        }, { syncCRDT: false });
      }
    },

    // Cleanup function
    cleanup: () => {
      const state = get();
      if (state.cleanup) {
        state.cleanup();
      }
      try {
        if (state.docId) {
          // Ensure CRDT provider/doc are disconnected for this doc
          docsCRDTService.cleanup(state.docId);
          const log = createFileLogger('DOCS_APP_STORE');
          log.log('[DocsAppStore] cleanup: Disconnected CRDT for docId:', state.docId);
        }
      } catch {}
    },

    // Fetch recent docs
    fetchRecentDocs: async () => {
      try {
        get()._applyLocalUpdate({ lifecycle: 'loading' }, { syncCRDT: false, log: false });
        
        // For unauthenticated users, skip fetching recent docs (they don't have a document list)
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          log.info('[DocsAppStore] Skipping recent docs fetch for unauthenticated user');
          get()._applyLocalUpdate({ 
            recentDocs: [],
            lifecycle: 'ready'
          }, { syncCRDT: false, log: false });
          return;
        }
        
        // ✅ PERFORMANCE FIX: Read from EntityStore instead of fetching from API!
        const useEntityStore = (await import('../../../stores/entityStore')).default;
        const { entities } = useEntityStore.getState();
        const docs = entities.filter((e: any) => e.entityType === 'Doc');
        
        log.info('[DocsAppStore] ✅ Loaded recent docs from EntityStore cache (no API call!)', {
          totalDocs: docs.length
        });
        
        // order by created_at or updated_at (favour the updated_at)
        const sortedDocs = docs.sort((a, b) => {
          const aUpdatedAt = new Date(a.updated_at).getTime();
          const bUpdatedAt = new Date(b.updated_at).getTime();
          return bUpdatedAt - aUpdatedAt;
        });
        
        // Ensure all docs have the 📄 icon
        const docsWithIcons = sortedDocs.map(doc => ({
          ...doc,
          icon: doc.icon || '📄'
        }));
        
        get()._applyLocalUpdate({ 
          recentDocs: docsWithIcons as DocEntity[],
          lifecycle: 'ready'
        }, { syncCRDT: false, log: false });
      } catch (error) {
        log.error('[DocsAppStore] Error fetching recent docs:', error);
        get()._applyLocalUpdate({ 
          error: error as Error,
          lifecycle: 'error'
        }, { syncCRDT: false, log: false });
      }
    },

    // Create doc
    createDoc: async () => {
      try {
        const { spaceId } = get(); // Get spaceId from store state
        const docMiniId = generateDocumentPrefix();
        const newDocEntityData = {
          entityType: 'Doc',
          name: 'New Document',
          icon: '📄', // Set the document icon
          spaceId: spaceId, // ✅ FIX: Add spaceId to associate document with space
          skeleton: {
            '@type': 'Doc',
            content: '',
            docMiniId, // Add document prefix for node IDs
            temporary: false, // Ensure documents created directly are permanent
            settings: {
              isContentsVisible: true,
              isChatVisible: true
            }
          },
          created_at: new Date().toISOString(),
        }
        const { id } = await entityService.create(newDocEntityData);
        const newDocEntity = { 
          ...newDocEntityData, 
          id,
          _id: id, // Add missing _id property
          type: 'doc', // Add missing type property  
          updated_at: new Date().toISOString() // Add missing updated_at property
        } as DocEntity;
        
        // ✅ PERFORMANCE FIX: Removed validation GET request
        // If entityService.create() succeeded, document exists - no need to fetch again!
        
        log.log('[DocsAppStore] ✅ Document created successfully:', {
          docId: id,
          name: newDocEntity.name,
          entityType: newDocEntity.entityType
        });
        
        get()._applyLocalUpdate({ recentDocs: [...get().recentDocs, newDocEntity] }, { syncCRDT: false, log: false });
        
        
        return newDocEntity;
      } catch (error) {
        log.error('[DocsAppStore] Error creating doc:', error);
        get()._applyLocalUpdate({ error: error as Error }, { syncCRDT: false, log: false });
        throw error;
      }
    },

    // Add subscribe method directly in the store
    subscribe: (subscriber: (state: DocsAppState) => void) => {
      subscribers.add(subscriber);
      return () => subscribers.delete(subscriber);
    },


    // Diff interface (with nodes)

    getNodes: (view?: EditorView) => {
      const editorView = view || get().editorView;
      if (!editorView) return [];
      
      const yxmlNodes: YxmlNode[] = [];
      editorView.state.doc.descendants((node, pos) => {
        if (node.isBlock) {
          log.log('[DocsAppStore][getNodes] Found block node:', { node, pos });
          yxmlNodes.push({
            content: node.textContent,
            type: node.type.name,
            pos,
            size: node.nodeSize,
            isDeletion: node.attrs.change_status === 'old',
            isInsertion: node.attrs.change_status === 'new'
          });
        }
      });
      log.log('[DocsAppStore][getNodes] YXML nodes:', yxmlNodes);
      return yxmlNodes;
    },

    addNode: (node: Partial<YxmlNode>) => {
      log.log('[DocsAppStore][addNode] Adding yxml node:', { node });
      
      if (!node.content || !node.type || node.pos === undefined) return null;

      const { editorView, docId } = get();
      if (!editorView) {
        log.error('[DocsAppStore][addNode] No editor view found');
        return null;
      }

      // Check if editor is visible to prevent DOM inconsistency errors
      const editorContainer = editorView.dom?.closest('[data-window-id]');
      const isVisible = editorContainer && window.getComputedStyle(editorContainer).display !== 'none';
      if (!isVisible) {
        log.warn('[DocsAppStore][addNode] Skipping addNode - editor is hidden');
        return null;
      }

      try {
        const { state, dispatch } = editorView;
        const { tr } = state;

        // create new node
        const newNode = state.schema.nodes[node.type].create(
          { change_status: 'new', revision_id: new ObjectId().toString() },
          state.schema.text(node.content)
        );

        log.log('[DocsAppStore][addNode] Adding new node:', { newNode });

        // insert new node at position
        tr.insert(node.pos, newNode);
        
        // Dispatch transaction
        dispatch(tr);
        
        // Get nodes from the current editor state (after dispatch)
        const { getNodes } = get();
        const nodes = getNodes(editorView);
        log.log('[DocsAppStore][addNode] Nodes after insert:', { nodes });
        
        const textContent = editorView.state.doc.textContent;
        get()._applyLocalUpdate({ docContent: textContent }, { syncCRDT: false, log: false });
        
        // Persist changes to backend immediately - CRDT service will preserve change attributes
        const ydoc = get().ydoc;
        const yxml = yDocumentService.getXmlFragment(ydoc, 'prosemirror', {
          windowId: get().windowId,
          docId: docId || undefined,
          logPrefix: '[DocsAppStore.addNode]'
        });
        
        if (docId && yxml) {
          log.log('[DocsAppStore][addNode][ai_debug] 💾 Persisting to CRDT:', {
            docId,
            windowId: get().windowId,
          });
          docsCRDTService.saveToDB(docId, yxml);
          log.log('[DocsAppStore][addNode][ai_debug] ✅ CRDT save completed');
        }
        
        return nodes;
      } catch (error) {
        log.error('[DocsAppStore][addNode] Error adding node:', error);
        return null;
      }
    },

    removeNode: (position: number) => {
      const { editorView, docId } = get();
      if (!editorView) return null;

      // Check if editor is visible to prevent DOM inconsistency errors
      const editorContainer = editorView.dom?.closest('[data-window-id]');
      const isVisible = editorContainer && window.getComputedStyle(editorContainer).display !== 'none';
      if (!isVisible) {
        log.warn('[DocsAppStore][removeNode] Skipping removeNode - editor is hidden');
        return null;
      }
      
      try {
        const { state, dispatch } = editorView;
        const { tr } = state;

        // find node at position
        const node = state.doc.nodeAt(position);
        if (!node) return null;

        if (node.isText) {
          // For text nodes, find the parent node position
          const $pos = state.doc.resolve(position);
          const parentPos = $pos.before($pos.depth);
          const parentNode = state.doc.nodeAt(parentPos);
          
          if (parentNode) {
            // Mark the parent node as old
            tr.setNodeMarkup(parentPos, null, { change_status: 'old', revision_id: new ObjectId().toString() });
          } else {
            throw new Error('[DocsApp - Store] Parent node not found');
          }
        } else {
          // For non-text nodes, mark directly
          tr.setNodeMarkup(position, null, { change_status: 'old', revision_id: new ObjectId().toString() });
        }

        dispatch(tr);
        
        const { getNodes } = get();
        const nodes = getNodes(editorView);
        log.log('[DocsAppStore][removeNode] Nodes after remove:', { nodes });
        
        // Persist changes to backend immediately
        const ydoc = get().ydoc;
        const yxml = yDocumentService.getXmlFragment(ydoc, 'prosemirror', {
          windowId: get().windowId,
          docId: docId || undefined,
          logPrefix: '[DocsAppStore.removeNode]'
        });
        
        if (docId && yxml) {
          docsCRDTService.saveToDB(docId, yxml);
        }
        
        return nodes;
      } catch (error) {
        log.error('[DocsAppStore][removeNode] Error removing node:', error);
        return null;
      }
    },

    updateNode: (node: Partial<YxmlNode>) => {
      if (!node.content || !node.type || node.pos === undefined) return null;
      const { editorView, docId } = get();
      if (!editorView) return null;

      // Check if editor is visible to prevent DOM inconsistency errors
      const editorContainer = editorView.dom?.closest('[data-window-id]');
      const isVisible = editorContainer && window.getComputedStyle(editorContainer).display !== 'none';
      if (!isVisible) {
        log.warn('[DocsAppStore][updateNode] Skipping updateNode - editor is hidden');
        return null;
      }
    
      try {
        const { state, dispatch } = editorView;
        const { tr } = state;

        const revisionId = new ObjectId().toString();

        // find node at position
        const oldNode = state.doc.nodeAt(node.pos);
        if (!oldNode) return null;

        if (oldNode.isText) {
          // For text nodes, find the parent node position
          const $pos = state.doc.resolve(node.pos);
          const parentPos = $pos.before($pos.depth);
          const parentNode = state.doc.nodeAt(parentPos);
          
          if (parentNode) {
            // Mark the parent node as old
            tr.setNodeMarkup(parentPos, null, { change_status: 'old', revision_id: revisionId });
          } else {
            throw new Error('[DocsApp - Store] Parent node not found');
          }
        } else {
          // For non-text nodes, mark directly
          tr.setNodeMarkup(node.pos, null, { change_status: 'old', revision_id: revisionId });
        }

        // create new node
        const newNode = state.schema.nodes[node.type].create(
          { change_status: 'new', revision_id: revisionId },
          state.schema.text(node.content)
        );

        // insert new node at position
        tr.insert(node.pos, newNode);
        dispatch(tr);
        
        const { getNodes } = get();
        const nodes = getNodes(editorView);
        log.log('[DocsAppStore][updateNode] Nodes after update:', { nodes });
        
        const textContent = editorView.state.doc.textContent;
        get()._applyLocalUpdate({ docContent: textContent }, { syncCRDT: false, log: false });
        
        // Persist changes to backend immediately
        const ydoc = get().ydoc;
        const yxml = yDocumentService.getXmlFragment(ydoc, 'prosemirror', {
          windowId: get().windowId,
          docId: docId || undefined,
          logPrefix: '[DocsAppStore.updateNode]'
        });
        
        if (docId && yxml) {
          docsCRDTService.saveToDB(docId, yxml);
        }
        
        return nodes;
      } catch (error) {
        log.error('[DocsAppStore][updateNode] Error updating node:', error);
        return null;
      }
    },

    // Summary methods
    updateSummary: async (force = false) => {
      const { docId, editorViewGetCurrentText } = get();
      if (!docId) {
        log.warn('[DocsAppStore] Cannot update summary: no docId');
        return;
      }

      try {
        const content = editorViewGetCurrentText();
        if (force) {
          log.log(`[DocsAppStore] Force updating summary for doc ${docId.slice(-8)}`);
          await summaryService.updateSummary(docId, content);
        } else {
          // Get current summary from recentDocs or fetch it
          const currentDoc = get().recentDocs.find(d => d.id === docId);
          log.log(`[DocsAppStore] Scheduling summary update for doc ${docId.slice(-8)}`);
          summaryService.scheduleSummaryUpdate(docId, content, currentDoc?.skeleton?.summary);
        }
      } catch (error) {
        log.error('[DocsAppStore] Summary update failed:', error);
      }
    },

    getSummaryStatus: () => {
      const { docId, recentDocs } = get();
      if (!docId) return { pending: false };

      const currentDoc = recentDocs.find(d => d.id === docId);
      return {
        pending: summaryService.getPendingStatus(docId),
        summary: currentDoc?.skeleton?.summary
      };
    },

    // ✅ CRITICAL FIX: Return getter that returns SAME object instance
    // OLD: getter created new object every render → reference instability → effect re-runs
    // NEW: memoized object reference → stable across renders
    get actions() {
      // Create actions object once and cache it on the store instance
      const store = get();
      if (!(store as any)._actionsCache) {
        log.log(`✅ [STORE_CREATE] Creating STABLE actions object for store`, {
          windowId,
          spaceId
        });
        
        // ✅ TypeScript fix: Return direct method references instead of wrapping
        // This avoids spread operator issues and maintains 'this' binding
        (store as any)._actionsCache = {
          initialize: store.initialize,
          createDoc: store.createDoc,
          fetchRecentDocs: store.fetchRecentDocs,
          setDocId: store.setDocId,
          setZoomLevel: store.setZoomLevel,
          zoomIn: store.zoomIn,
          zoomOut: store.zoomOut,
          resetZoom: store.resetZoom,
          updateSummary: store.updateSummary,
          addNode: store.addNode,
          removeNode: store.removeNode,
          updateNode: store.updateNode,
          setEditorView: store.setEditorView,
          editorViewGetCurrentText: store.editorViewGetCurrentText,
        };
      }
      
      return (store as any)._actionsCache;
    },
  }));
};

// Create a map to store instances
const storeMap = new Map<string, ReturnType<typeof createDocsAppStore>>();

// Get or create store instance
export const getDocsAppStore = (windowId: string, spaceId: string) => {
  const key = `${windowId}-${spaceId}`;

  if (!storeMap.has(key)) {
    const store = createDocsAppStore(windowId, spaceId);
    storeMap.set(key, store);
  }
  return storeMap.get(key)!;
};

// Hook to use the store - returns the store hook itself which supports selectors
export function useDocsAppStore(windowId: string, spaceId: string): UseBoundStore<StoreApi<DocsStore>> {
  return useMemo(() => getDocsAppStore(windowId, spaceId), [windowId, spaceId]);
}

// Cleanup function
export const cleanupDocsStore = (windowId: string, spaceId: string) => {
  const key = `${windowId}-${spaceId}`;
  const store = storeMap.get(key);
  if (store) {
    const state = store.getState();
    state.cleanup();
    storeMap.delete(key);
    log.log('[DocsAppStore] Cleaned up store for:', key);
  }
};
